import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';

import { UserService } from './user.service';
import { FileService } from './file.service';
import { ToastComponent } from '../shared/toast/toast.component';
import { User } from '../shared/models/user.model';
import { ee } from '../shared/constants';
import { EmailAndPassword } from '../shared/types';

@Injectable()
export class AuthService {
  private userService = inject(UserService);
  private fileService = inject(FileService);
  private router = inject(Router);
  private jwtHelper = inject(JwtHelperService);
  toast = inject(ToastComponent);

  loggedIn = false;
  isAdmin = false;

  currentUser: User = new User();

  constructor() {
    const token = localStorage.getItem('token');
    if (token && !this.jwtHelper.isTokenExpired(token)) {
      const decodedUser = this.decodeUserFromToken(token);
      if (!decodedUser) {
        this.logout(); // Malformed or invalid JWT token
      } else {
        this.setCurrentUser(decodedUser);
      }
    } else if (token && this.jwtHelper.isTokenExpired(token)) {
      this.sessionExpired();
    }
  }

  login(emailAndPassword: EmailAndPassword): void {
    this.userService.login(emailAndPassword)
      .subscribe({
        next: res => {
          localStorage.setItem('token', res.token);
          const decodedUser = this.decodeUserFromToken(res.token);
          this.setCurrentUser(decodedUser);
          this.setCurrentVersion();
          this.loggedIn = true;
          this.router.navigate(['/']);
        },
        error: err => this.toast.setMessage(err.error.message, 'danger')
      });
  }

  logout(): void {
    ee.removeAllListeners();
    localStorage.removeItem('token');
    this.loggedIn = false;
    this.isAdmin = false;
    this.currentUser = new User();
    this.router.navigate(['/']);
  }

  decodeUserFromToken(token): object {
    return this.jwtHelper.decodeToken(token).user;
  }

  setCurrentUser(decodedUser): void {
    this.loggedIn = true;
    this.currentUser = decodedUser;
    this.isAdmin = decodedUser.role === 'admin';
    delete decodedUser.role;
  }

  setCurrentVersion(): void {
    this.fileService.getVersion()
      .subscribe({
        next: res => localStorage.setItem('version', res),
        error: err => err.error.message === 'Invalid or expired token' ? location.reload() : this.toast.setMessage(err.error.message, 'danger')
      });
  }

  sessionExpired(): void {
    this.logout();
    this.router.navigate(['/login']);
    return this.toast.setMessage('Session expired, please login again.', 'warning');
  }

}
